import React, { useEffect, useRef, useState } from "react";
import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import handleError from "../../../constants/handleError";
import QRCode from "qrcode";
import jsPDF from "jspdf";
import { useAuth } from "../../../context/AuthProvider";
import { usePermission } from "../../../context/PermissionContext";
import uRLs from "../../../utils/baseUtils";
import { CircularProgress } from "@mui/material";
import { AndroidIcon, AppleIcon, CopyIcon, DownloadIcon, EditIcon, MobileAltIcon, SaveIcon, XIcon } from "../../../constants/Icons";

function PaymentControl() {
  const [loading, setLoading] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [platform, setPlatform] = useState("");
  const [downloadQr, setDownloadQr] = useState("");
  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const { updateSettings } = useAuth()
  const {  storeSettings } =usePermission();
  const [upi, setUpi] = useState("");
  const [upiQrLink, setUpiQrLink] = useState("");
  const [accountNo, setAccountNo] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [beneficiaryName, setBeneficiaryName] = useState("");
  const [branchName, setBranchName] = useState("");
  const [bankName, setBankName] = useState("");
  const [errors, setErrors] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditingBank, setIsEditingBank] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");
  const [generatedUpiId, setGeneratedUpiId] = useState("");
  const [originalBankData, setOriginalBankData] = useState({});
  const upiRef = useRef(null);
  const accountNoRef = useRef(null);
  const bankRef = useRef(null);
  const ifscRef = useRef(null);
  const branchNameRef =useRef(null)
  const beneficiaryRef = useRef(null);

  const fetchPaymentSettings = async () => {
    try {
      if (!checkNetwork()) return;

      const response = await AxiosInstance.get("module/storesettings");
      const paymentData = response?.data?.results?.paymentSettings;

      if (paymentData) {
        setUpi(paymentData?.upiId || "");
        setUpiQrLink(paymentData?.upiQrLink || "");
        setAccountNo(paymentData?.bankDetails?.accountNo || "");
        setIfsc(paymentData?.bankDetails?.ifsc || "");
        setBeneficiaryName(paymentData?.bankDetails?.beneficiaryName || "");
        setBranchName(paymentData?.bankDetails?.branch || "");
        setBankName(paymentData?.bankDetails?.bankName || "")
      }
    } catch (error) {
      handleError(error, showAlert);
    }
  };

  const validateForm = () => {
    let newErrors = {};

    // UPI validation (optional, only if entered)
    if (upi && !/^[\w.\-]+@[\w]+$/.test(upi)) {
      newErrors.upi = "Enter a valid UPI ID (e.g. abc@upi)";
    }

    // Account number validation (optional, but if entered, must be 9-18 digits)
    if (accountNo && !/^\d{9,18}$/.test(accountNo)) {
      newErrors.accountNo = "Account No must be 9 to 18 digits";
    }

    // IFSC validation (optional, but if entered, must follow proper format)
    if (ifsc && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) {
      newErrors.ifsc = "Enter a valid IFSC code (e.g. HDFC0001234)";
    }

    // Beneficiary Name validation (optional, but if entered, must be at least 2 chars)
    if (beneficiaryName && beneficiaryName.trim().length < 2) {
      newErrors.beneficiaryName =
        "Beneficiary name must be at least 2 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Returns true if no errors
  };
  useEffect(() => {
    let qrValue = "";
    if (upiQrLink?.trim()) {   
      qrValue = upiQrLink;
    } else if (upi?.trim()) {  
      qrValue = `upi://pay?pa=${upi}&pn=${beneficiaryName || "Merchant"}&cu=INR`;
    } else {
      setQrCodeDataUrl("");
      setGeneratedUpiId("");
      return;
    }
    QRCode.toDataURL(qrValue)
      .then((url) => {
        setQrCodeDataUrl(url);
        setGeneratedUpiId(upi);
      })
    .catch((err) => {
      console.error("QR Code generation failed:", err);
    });
 }, [upi, upiQrLink, beneficiaryName]);

  const handleSavePayment = async () => {
    if (!validateForm()) return;
    try {
      if (!checkNetwork()) return;

      const paymentSettings = {};
      if (upi?.trim()) paymentSettings.upiId = upi.trim();

      const bankDetails = {};
      if (accountNo?.trim()) bankDetails.accountNo = accountNo.trim();
      if (ifsc?.trim()) bankDetails.ifsc = ifsc.trim();
      if (beneficiaryName?.trim())
        bankDetails.beneficiaryName = beneficiaryName.trim();
        bankDetails.bankName = bankName?.trim() || "";
        bankDetails.branch = branchName?.trim() || "";
      if (Object.keys(bankDetails).length > 0) {
        paymentSettings.bankDetails = bankDetails;
      }

      const data = {};
      if (Object.keys(paymentSettings).length > 0) {
        data.paymentSettings = paymentSettings;
      }

      setLoading(true);
      const response = await AxiosInstance.patch("module/storesettings", data);
      updateSettings({
      storeSettings: data
      }); 
      showAlert("success", response.data.message);
    } catch (error) {
      handleError(error, showAlert);
    } finally {
      setLoading(false);
    }
  };

  const handleEnterKey = (e, fieldName, nextRef = null, isLast = false) => {
    if (e.key === "Enter") {
      e.preventDefault();
      let fieldError = "";
      switch (fieldName) {
        case "upi":
          if (!upi.trim()) fieldError = "UPI ID is required";
          else if (!/^[\w.\-]+@[\w]+$/.test(upi))
            fieldError = "Enter a valid UPI ID (e.g. abc@upi)";
          break;
        case "accountNo":
          if (!accountNo.trim()) fieldError = "Account Number is required";
          else if (!/^\d{9,18}$/.test(accountNo))
            fieldError = "Account No must be 9 to 18 digits";
          break;
        case "ifsc":
          if (!ifsc.trim()) fieldError = "IFSC Code is required";
          else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc))
            fieldError = "Enter a valid IFSC code (e.g. HDFC0001234)";
          break;
        case "beneficiaryName":
          if (!beneficiaryName.trim())
            fieldError = "Beneficiary name is required";
          else if (beneficiaryName.trim().length < 2)
            fieldError = "Beneficiary name must be at least 2 characters";
          break;
        default:
          break;
      }
      if (fieldError) {
        setErrors((prev) => ({ ...prev, [fieldName]: fieldError }));
        return;
      } else {
        setErrors((prev) => ({ ...prev, [fieldName]: "" }));
      }
      if (!isLast) {
        nextRef?.current?.focus();
      } else {
        handleSavePayment();
      }
    }
  };

  const downloadQrPdf = () => {
    if (!qrCodeDataUrl) return;
    const doc = new jsPDF("p", "pt", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;
    doc.setFontSize(14);
    doc.text("Money Payment", pageWidth / 2, y, { align: "center" });
    y += 22;
    doc.setFontSize(18);
    doc.text("Scan to Pay", pageWidth / 2, y, { align: "center" });
    y += 15;
    const qrWidth = 150;
    const qrX = (pageWidth - qrWidth) / 2;
    doc.addImage(qrCodeDataUrl, "PNG", qrX, y, qrWidth, qrWidth);
    y += qrWidth + 20;
    doc.setFontSize(14);
    doc.text(`UPI ID: ${generatedUpiId}`, pageWidth / 2, y, {
      align: "center",
    });
    y += 30;
    doc.setFontSize(14);
    doc.text("THANK YOU!", pageWidth / 2, y, { align: "center" });
    y += 20;
    doc.setFontSize(12);
    if (storeSettings?.negobillBranding) {
    doc.text("Powered by NegoBill.com", pageWidth / 2, y, { align: "center" });
    }
     doc.save("QRCode.pdf");
  };
 
  useEffect(() => {
  if (!showQrModal) return;

  const link =
    platform === "android"
      ? uRLs.PLAY_STORE_LINK
      : uRLs.APP_STORE_LINK;

  QRCode.toDataURL(link)
    .then((url) => setDownloadQr(url))
    .catch((err) => console.log(err));
}, [showQrModal, platform]);

  useEffect(() => {
    fetchPaymentSettings();
  }, []);

  const handleCancel = () => {
    setAccountNo(originalBankData.accountNo || "");
    setIfsc(originalBankData.ifsc || "");
    setBeneficiaryName(originalBankData.beneficiaryName || "");
    setBankName(originalBankData.bankName || "");
    setBranchName(originalBankData.branchName || "");

    setIsEditingBank(false);
  };
  return (<>
    <div className="row">
      <div className="col-lg-12">
        <div className="card">
          <div className="card-header p-3 bg-light-core d-flex align-item-center justify-content-between">
            <h6 className="mb-0 text-dark ">Payment Settings</h6>            
              {/* <button
                type="button"
                className="btn btn-primary d-flex align-items-center gap-1 py-2"
                 onClick={() => setShowEditModal(true)}
              >
                <i className="ng ng-ng-edit-icon me-1"></i> Edit Payment
             </button>             */}
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-lg-6 pb-2">
                <label htmlFor="upi" className="form-label">
                  UPI ID <span className="text-danger">*</span>
                </label>
              <div className="input-group"> 
                <input
                  type="text"
                  name="upi"
                  autoFocus
                  ref={upiRef}
                  disabled
                  autoComplete="off"
                  value={upi}
                  onChange={(e) => setUpi(e.target.value)}
                  onClick={() => setShowEditModal(true)}
                  className="form-control"
                  onFocus={(e) => e.target.select()}
                  onKeyDown={(e) => handleEnterKey(e, "upi", accountNoRef)}
                />
                 <button className="input-group-text" onClick={() => setShowEditModal(true)}>
                   <EditIcon/>
                 </button> 
               </div>  
                {/* Bank Details */}
                <div className="card mt-3">
                <div className="card-header p-2 d-flex justify-content-between align-items-center">
                  <h6 className="mb-0 px-2">Account Details</h6>
                  <div>
                  {!isEditingBank ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                     onClick={() => {
                      setOriginalBankData({
                        accountNo,
                        ifsc,
                        beneficiaryName,
                        bankName,
                        branchName,
                      });

                      setIsEditingBank(true);
                    }}
                    >
                      <EditIcon iClass={"me-1"}/>
                      Edit
                    </button>
                  ) : (<>
                    {loading ? (
                     <button type="button" className="btn btn-primary   mt-0" disabled>
                     <CircularProgress size={18} className="text-white" />
                     </button>
                   ) : (<>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={async () => {
                        await handleSavePayment();
                        setIsEditingBank(false);
                      }}
                    >
                      <SaveIcon iClass={"me-1"}/>
                      Save
                    </button>
                     <button
                    className="btn  btn-outline-secondary btn-sm ms-2 p-2"
                    onClick={handleCancel}
                  >
                     <XIcon/> 
                  </button>
                  </>
                   )}
                   
                   </>
                  )}
                  </div>
                </div>

                <div className="card-body">
                <div className="mt-2">
                  <label className="form-label">
                    Account Number <span className="text-danger">*</span>{" "}
                  </label>
                  <input
                    type="text"
                    className="form-control"                    
                    value={accountNo}
                    disabled={!isEditingBank}
                    ref={accountNoRef}
                    onChange={(e) => setAccountNo(e.target.value)}
                    onKeyDown={(e) => handleEnterKey(e, "accountNo", ifscRef)}
                  />
                  {errors.accountNo && (
                    <div className="invalid-feedback">{errors.accountNo}</div>
                  )}
                  <label className="form-label mt-2">
                    IFSC Code <span className="text-danger">*</span>{" "}
                  </label>
                  <input
                    type="text"
                    className="form-control"  
                    disabled={!isEditingBank}                   
                    value={ifsc}
                    ref={ifscRef}
                    onChange={(e) => setIfsc(e.target.value)}
                    onKeyDown={(e) => handleEnterKey(e, "ifsc",beneficiaryRef )}
                  />
                  {errors.ifsc && (
                    <div className="invalid-feedback">{errors.ifsc}</div>
                  )}
                   <label className="form-label mt-2">
                    Beneficiary Name <span className="text-danger">*</span>{" "}
                  </label>
                  <input
                    type="text"
                    className="form-control"                    
                    value={beneficiaryName}
                    disabled={!isEditingBank}  
                    ref={beneficiaryRef}
                    onChange={(e) => setBeneficiaryName(e.target.value)}
                    onKeyDown={(e) =>
                      handleEnterKey(e, "beneficiaryName", bankRef)
                    }
                  />
                  {errors.beneficiaryName && (
                    <div className="invalid-feedback">
                      {errors.beneficiaryName}
                    </div>
                  )} 
                 
                  <label className="form-label mt-2">
                    Bank Name <span className="text-danger"></span>{" "}
                  </label>
                  <input
                    type="text"
                    className="form-control"                    
                    value={bankName}
                    disabled={!isEditingBank}
                    ref={bankRef}
                    onChange={(e) => setBankName(e.target.value)}
                    onKeyDown={(e) =>
                      handleEnterKey(e, "bankName", branchNameRef)
                    }
                  />
                  {errors.bankName && (
                    <div className="invalid-feedback">
                      {errors.bankName}
                    </div>
                  )}
                    <label className="form-label mt-2">
                    Branch Name<span className="text-danger"></span>{" "}
                  </label>
                  <input
                    type="text"
                    className="form-control"                    
                    value={branchName}
                    disabled={!isEditingBank}  
                    ref={branchNameRef}
                    onChange={(e) => setBranchName(e.target.value)}
                    onKeyDown={(e) =>
                      handleEnterKey(e, "beneficiaryName", null, true)
                    }
                  />
                  {errors.branchName && (
                    <div className="invalid-feedback">
                      {errors.branchName}
                    </div>
                  )}               
                  {/* <div className="text-end">
                    {loading ? (
                     <button type="button" className="btn btn-primary   mt-3" disabled>
                     <CircularProgress size={18} className="text-white" />
                     </button>
                   ) : (
                    <button
                      type="button"
                      className="btn btn-primary mt-3"
                      onClick={handleSavePayment}
                      disabled={loading}
                    >
                     <i className="bx bx-save me-1"></i>  Save
                    </button>
                   )}
                  </div> */}
                </div>
                </div>
                </div>
              </div>

              {/* QR Code Display */}
              <div className="col-lg-6">
                {qrCodeDataUrl && (
                  <>
                    <div className="card p-2 bg-light-blue">
                      <div className="d-flex justify-content-center">
                        <span>Money Payment</span>
                      </div>
                      <div className="d-flex justify-content-center">
                        <h3>Scan to Pay</h3>
                      </div>
                      <div className="d-flex justify-content-center">
                        <img
                          src={qrCodeDataUrl}
                          alt="QR Code"
                          className="rounded"
                          style={{ width: "200px", height: "200px" }}
                        />
                      </div>
                      <div
                        className="d-flex justify-content-center align-items-center"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          if (generatedUpiId) {
                            navigator.clipboard
                              .writeText(generatedUpiId)
                              .then(() => {
                                showAlert("success", "UPI ID copied!");
                              });
                          }
                        }}
                        title="Copy UPI ID"
                      >
                        <span>UPI ID: {generatedUpiId}</span>
                        <CopyIcon iClass={"fs-18 ms-2"}/>
                      </div>
                      <div className="d-flex justify-content-center">
                        <h5 className="pt-1">THANK YOU!</h5>
                      </div>
                      {storeSettings?.negobillBranding &&(
                      <div className="d-flex justify-content-center">
                        Powered by 
                         <img
                              src="/assets/images/logo-dark.png"
                              className="mx-100 ms-2"
                              alt="logo icon"
                            />
                        <span className="text-primary">.com</span>                          
                      </div>
                      )}
                      <div>
                        <button
                          className="btn btn-warning btn-sm text-right"
                          onClick={downloadQrPdf}
                        >
                          <DownloadIcon iClass={"fs-15"}/>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              {/* <div>
                <button
                  type="button"
                  className="btn btn-primary text-right"
                  onClick={handleSavePayment}
                  disabled={loading}
                >
                  <i className="bx bx-check me-2"></i>Save
                </button>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
    {showEditModal && (
    <>
        <div className="modal fade show d-block">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            Manage Payment Settings
                        </h5>
                        <button
                            className="btn-close border"
                            onClick={() => setShowEditModal(false)}
                        />
                    </div>

                    <div className="modal-body text-center">
                        <MobileAltIcon iClass={"font-60"}/>
                        <h6 className="mt-3">
                            Payment Settings can only be updated using the
                            NegoBill Mobile App.
                        </h6>
                        <p className="text-muted">
                            Please choose your device.
                        </p>
                        <div className="d-flex justify-content-center gap-3 mt-4 mb-3">
                        <button
                          className="btn btn-success"
                          onClick={() => {
                            setPlatform("android");
                            setShowEditModal(false);
                            setShowQrModal(true);
                          }}
                        >
                          <AndroidIcon iClass={"me-2"}/>
                          Android
                        </button>

                         <button
                          className="btn btn-dark"
                          onClick={() => {
                            setPlatform("ios");
                            setShowEditModal(false);
                            setShowQrModal(true);
                          }}
                        >
                          <AppleIcon iClass={"me-2"}/>
                          iOS
                        </button>

                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div className="modal-backdrop fade show"></div>
    </>
)}
{showQrModal && (
  <>
    <div className="modal fade show d-block">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">

          <div className="modal-header">
            <h5 className="modal-title">
              Download NegoBill App
            </h5>

            <button
              className="btn-close border"
              onClick={() => setShowQrModal(false)}
            />
          </div>

          <div className="modal-body text-center">

            <h6>
              Scan the QR code to download the{" "}
              {platform === "android"
                ? "Android"
                : "iOS"}{" "}
              app and update your payment settings.
            </h6>

            {downloadQr && (
              <img
                src={downloadQr}
                alt="Download QR"
                style={{
                  width: 220,
                  height: 220,
                }}
              />
            )}

            <div className="mt-3">
              <button
                className="btn btn-primary"
                onClick={() =>
                  window.open(
                    platform === "android"
                      ? uRLs.PLAY_STORE_LINK
                      : uRLs.APP_STORE_LINK,
                    "_blank"
                  )
                }
              >
                Open {platform === "android"
                  ? "Play Store"
                  : "App Store"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="modal-backdrop fade show"></div>
  </>
)}
    </>
  );
}

export default PaymentControl;
